import { REVIEWEE_FILTER_QUERY_KEYS, useRevieweeFilters } from "@domains/reviews/client/components/reviewee-filters/useRevieweeFilters.ts";
import { createParser, parseAsArrayOf, parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs";
import { useCallback, useMemo } from "react";
import type { ComparisonInput } from "#shared/comparisonSelection.ts";
import { type PeriodSelection, ReviewHistoryFilters } from "#shared/periodSelection.ts";

const ISO_CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const DEFAULT_REVIEW_HISTORY_FILTER = "LastYear";

const ALL_CYCLES_PARAM = "all";

const ReviewHistoryFilterParams = [...ReviewHistoryFilters, ALL_CYCLES_PARAM] as const;

interface PeriodParams {
    reviewCycleIds: string[];
    reviewHistoryFilter: (typeof ReviewHistoryFilterParams)[number] | null;
    from: string | null;
    to: string | null;
}

export function resolvePeriodSelection({ reviewCycleIds, reviewHistoryFilter, from, to }: PeriodParams): PeriodSelection {
    const selection: PeriodSelection = {
        ...(reviewCycleIds.length > 0 && { reviewCycleIds }),
        ...(reviewHistoryFilter && reviewHistoryFilter !== ALL_CYCLES_PARAM && { reviewHistoryFilter }),
        ...(from && { from }),
        ...(to && { to })
    };

    return reviewHistoryFilter || Object.keys(selection).length > 0 ? selection : { reviewHistoryFilter: DEFAULT_REVIEW_HISTORY_FILTER };
}

const parseAsIsoCalendarDate = createParser({
    parse: value => (ISO_CALENDAR_DATE.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00.000Z`)) ? value : null),
    serialize: (value: string) => value
});

export const periodQuerySchema = {
    reviewCycleIds: parseAsArrayOf(parseAsString).withDefault([] as string[]),
    reviewHistoryFilter: parseAsStringLiteral(ReviewHistoryFilterParams),
    from: parseAsIsoCalendarDate,
    to: parseAsIsoCalendarDate
};

export const ANALYTICS_FILTER_QUERY_KEYS = [...REVIEWEE_FILTER_QUERY_KEYS, ...Object.keys(periodQuerySchema)];

const queryStateOptions = { history: "replace" as const, clearOnDefault: true, shallow: false };

export function useAnalyticsFilters() {
    const revieweeFilters = useRevieweeFilters();
    const clearRevieweeFilters = revieweeFilters.clearFilters;
    const [{ reviewCycleIds, reviewHistoryFilter, from, to }, setPeriodParams] = useQueryStates(periodQuerySchema, queryStateOptions);

    const periodSelection = useMemo(() => resolvePeriodSelection({ reviewCycleIds, reviewHistoryFilter, from, to }), [reviewCycleIds, reviewHistoryFilter, from, to]);

    const setPeriod = useCallback(
        (period: PeriodSelection) => {
            const isWholeHistory = !period.reviewCycleIds?.length && !period.reviewHistoryFilter && !period.from && !period.to;

            return void setPeriodParams({
                reviewCycleIds: period.reviewCycleIds?.length ? period.reviewCycleIds : null,
                reviewHistoryFilter: period.reviewHistoryFilter ?? (isWholeHistory ? ALL_CYCLES_PARAM : null),
                from: period.from ?? null,
                to: period.to ?? null
            });
        },
        [setPeriodParams]
    );

    const clearPeriod = useCallback(
        () =>
            void setPeriodParams({
                reviewCycleIds: null,
                reviewHistoryFilter: ALL_CYCLES_PARAM,
                from: null,
                to: null
            }),
        [setPeriodParams]
    );

    const hasActivePeriod = Object.keys(periodSelection).length > 0;

    const clearFilters = useCallback(() => {
        clearRevieweeFilters();
        clearPeriod();
    }, [clearRevieweeFilters, clearPeriod]);

    const baseSelection = useMemo<ComparisonInput>(() => ({ ...revieweeFilters.memberFilters, ...periodSelection }), [revieweeFilters.memberFilters, periodSelection]);

    const hasActiveFilters = revieweeFilters.hasActiveFilters || hasActivePeriod;

    return { ...revieweeFilters, periodSelection, setPeriod, clearPeriod, clearFilters, hasActivePeriod, baseSelection, hasActiveFilters };
}
